import { Box, HStack, Input, Stack, Text, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import {
  getIdealCustomerProfile,
  updateIdealCustomerProfile,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import PageBody from "../../../components/page-body";
import { toaster } from "../../../components/toaster";
import { useLoading } from "../../../hooks";
import { IdealCustomerProfile } from "../../../types/company-knowledge";

// Labels come from the original spec comments in
// audiences/components/company-profile.rkt (edited out alongside the
// migration); wording should be re-checked at review. All columns are
// nullable, so the schema carries no validators -- it exists for type
// inference and consistency with the form pattern.

const idealCustomerProfileSchema = z.object({
  description: z.string(),
  "business-models": z.string(),
  industries: z.string(),
  "company-size": z.string(),
  geographies: z.string(),
  "buyer-roles": z.string(),
  "buyer-needs": z.string(),
  disqualifiers: z.string(),
});

type IdealCustomerProfileFormData = z.infer<typeof idealCustomerProfileSchema>;

export default function IdealCustomerProfilePage() {
  const { workspace: workspaceSlug } = useParams();
  const [profile, setProfile] = React.useState<IdealCustomerProfile | null>(
    null,
  );
  const [, withLoading] = useLoading<void>(true);
  const [isSaving, withSaving] = useLoading<void>(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IdealCustomerProfileFormData>({
    defaultValues: emptyProfile,
    resolver: zodResolver(idealCustomerProfileSchema),
  });

  React.useEffect(() => {
    if (!workspaceSlug) return;
    void withLoading(async () => {
      setProfile(await getIdealCustomerProfile(workspaceSlug));
    });
  }, [withLoading, workspaceSlug]);

  React.useEffect(() => {
    if (!profile) return;
    reset(toFormData(profile));
  }, [profile, reset]);

  const onSubmit = handleSubmit(async (data) => {
    if (!workspaceSlug) return;
    await withSaving(async () => {
      await updateIdealCustomerProfile(workspaceSlug, data);
      toaster.create({
        type: "success",
        title: "Ideal customer profile updated",
      });
    });
  });

  return (
    <PageBody>
      <KnowledgeCard
        title="Ideal Customer Profile"
        sub="The baseline description of who you sell to; customer profile subsets inherit it."
      >
        <form onSubmit={onSubmit}>
          <Stack gap="1">
            <KnowledgeField
              label="Describe your ideal customer"
              error={errors.description?.message}
              input={
                <Textarea
                  {...register("description")}
                  placeholder="Include the nuances a new teammate would need to understand."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Company types and business models"
              error={errors["business-models"]?.message}
              input={
                <Textarea
                  {...register("business-models")}
                  placeholder="How your best customers run their business."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Industries"
              error={errors.industries?.message}
              input={
                <Textarea
                  {...register("industries")}
                  placeholder="The industries your ideal customers work in."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Company size"
              error={errors["company-size"]?.message}
              input={
                <Input
                  {...register("company-size")}
                  placeholder="Free text, e.g. headcount or revenue band"
                />
              }
            />
            <KnowledgeField
              label="Geographies"
              error={errors.geographies?.message}
              input={
                <Input
                  {...register("geographies")}
                  placeholder="Where your target customers are (not where campaigns run)"
                />
              }
            />
            <KnowledgeField
              label="Buyer roles and responsibilities"
              error={errors["buyer-roles"]?.message}
              input={
                <Textarea
                  {...register("buyer-roles")}
                  placeholder="Who buys what you sell, and what they own."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Needs and buying situations"
              error={errors["buyer-needs"]?.message}
              input={
                <Textarea
                  {...register("buyer-needs")}
                  placeholder="What they need, and when they go looking for it."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Who is not a fit?"
              error={errors.disqualifiers?.message}
              input={
                <Textarea
                  {...register("disqualifiers")}
                  placeholder="Customers who should not buy from you, and why."
                  rows={3}
                />
              }
            />
            <HStack marginTop="4">
              <Button loading={isSaving} type="submit" variant="solid">
                Update
              </Button>
            </HStack>
          </Stack>
        </form>
      </KnowledgeCard>
    </PageBody>
  );
}

const emptyProfile: IdealCustomerProfileFormData = {
  description: "",
  "business-models": "",
  industries: "",
  "company-size": "",
  geographies: "",
  "buyer-roles": "",
  "buyer-needs": "",
  disqualifiers: "",
};

// The schema's fields are nullable strings; empty strings are the
// form's representation of "unset".
const toFormData = (
  profile: IdealCustomerProfile,
): IdealCustomerProfileFormData => ({
  description: profile.description ?? "",
  "business-models": profile["business-models"] ?? "",
  industries: profile.industries ?? "",
  "company-size": profile["company-size"] ?? "",
  geographies: profile.geographies ?? "",
  "buyer-roles": profile["buyer-roles"] ?? "",
  "buyer-needs": profile["buyer-needs"] ?? "",
  disqualifiers: profile.disqualifiers ?? "",
});

// SettingsCard equivalent, local to this page for now.
function KnowledgeCard({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      background="white"
      borderRadius="l3"
      boxShadow="card"
      overflow="hidden"
    >
      <Box
        borderBottomWidth="1"
        borderColor="border.soft"
        paddingX="6"
        paddingY="4"
      >
        <Text color="fg.ink" fontSize="md" fontWeight="medium">
          {title}
        </Text>
        {sub && (
          <Text color="fg.muted" fontSize="sm" marginTop="0.5">
            {sub}
          </Text>
        )}
      </Box>
      <Box paddingBottom="6" paddingTop="5" paddingX="6">
        {children}
      </Box>
    </Box>
  );
}

// FormRow equivalent, local to this page for now.
function KnowledgeField({
  label,
  error,
  input,
}: {
  label: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <Stack gap="1" paddingY="2">
      <Text color="fg.muted" fontSize="sm">
        {label}
      </Text>
      {input}
      {error && (
        <Text color="red.500" fontSize="xs">
          {error}
        </Text>
      )}
    </Stack>
  );
}
